import { AxiosInstance } from 'axios';

/**
 * The diagnostics bundle of a scenario run: the state document as the runner wrote it, the suite it executed and
 * the evidence each scenario kept, as one JSON document with credentials masked.
 *
 * Written by hand next to the generated client, like the suite download: the bundle is passed on as the text the
 * API wrote, and running it through the generated types would only reformat it.
 */

const diagnosticsUrl = (customer: string, environment: string, runId: string) =>
    `/ScenarioRun/${encodeURIComponent(customer)}/${encodeURIComponent(
        environment
    )}/${encodeURIComponent(runId)}/diagnostics`;

/** Fetches the bundle of a run. */
export const fetchScenarioRunDiagnostics = async (
    axios: AxiosInstance,
    customer: string,
    environment: string,
    runId: string
): Promise<string> => {
    const response = await axios.get(
        diagnosticsUrl(customer, environment, runId),
        { headers: { Accept: 'application/json' }, responseType: 'text' }
    );

    // The axios instance is created with transformResponse: [], so this is the text as sent.
    return typeof response.data === 'string'
        ? response.data
        : JSON.stringify(response.data, null, 2);
};

/** The name the bundle is saved under. */
export const diagnosticsFileName = (runId: string) =>
    `scenario-run-${runId}-diagnostics.json`;

/** Saves a bundle as a file. */
export const saveScenarioRunDiagnostics = (content: string, runId: string) => {
    const url = URL.createObjectURL(
        new Blob([content], { type: 'application/json' })
    );
    const link = document.createElement('a');

    link.href = url;
    link.download = diagnosticsFileName(runId);
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
};

/**
 * Puts a bundle that is still being fetched on the clipboard.
 *
 * The clipboard is handed the pending content rather than the finished text where the browser allows it: Safari
 * and Firefox only let a page write to the clipboard right after a click, and the fetch can outlast that moment.
 * Where that is not supported the text is written once it is there, which Chrome accepts.
 */
export const copyScenarioRunDiagnostics = async (
    content: Promise<string>
): Promise<void> => {
    const clipboard = navigator.clipboard;

    if (!clipboard) {
        throw new Error('This browser does not give the page a clipboard.');
    }

    if (typeof ClipboardItem !== 'undefined' && clipboard.write) {
        await clipboard.write([
            new ClipboardItem({
                'text/plain': content.then(
                    (text) => new Blob([text], { type: 'text/plain' })
                ),
            }),
        ]);

        return;
    }

    await clipboard.writeText(await content);
};
